import { useState, useRef } from "react";
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogHeader,
} from "../../../components/ui/dialog";
import { Upload, X, Check, AlertTriangle, FileText, Info } from "lucide-react";
import { toast } from "react-toastify";

const { ipcRenderer } = window.require("electron");

export const ImportNumbersDialog = ({
	isOpen,
	onClose,
	onImport,
	numberType,
	isImporting
}) => {
	const [selectedFile, setSelectedFile] = useState(null);
	const [previewNumbers, setPreviewNumbers] = useState([]);
	const [selectedNumbers, setSelectedNumbers] = useState(new Set());
	const [validNumbers, setValidNumbers] = useState([]);
	const [invalidNumbers, setInvalidNumbers] = useState([]);
	const [showPreview, setShowPreview] = useState(false);
	const fileInputRef = useRef(null);

	const handleFileSelect = async (event) => {
		const file = event.target.files[0];
		if (!file) return;

		if (!file.name.endsWith('.txt')) {
			toast.error('Por favor, selecione um arquivo .txt');
			return;
		}

		setSelectedFile(file);

		try {
			const content = await file.text();
			const lines = content.split(/\r?\n/).filter(line => line.trim() !== '');

			const valid = [];
			const invalid = [];

			lines.forEach((line, index) => {
				const trimmed = line.trim();

				// Pula linhas vazias
				if (!trimmed) return;

				// Remove TODA formatação - apenas dígitos
				const onlyDigits = trimmed.replace(/\D/g, '');

				// Validação simplificada: aceita números brasileiros válidos
				let isValid = false;

				if (onlyDigits.length >= 8 && onlyDigits.length <= 15) {
					// Verifica se contém apenas caracteres válidos na linha original
					const hasOnlyValidChars = /^[\+\d\s\(\)\-\.]*$/.test(trimmed);

					if (hasOnlyValidChars && onlyDigits.length > 0) {
						isValid = true;
					}
				}

				if (isValid) {
					valid.push({
						number: onlyDigits, // Salva apenas dígitos
						originalNumber: trimmed, // Mantém original para exibição
						lineNumber: index + 1
					});
				} else {
					invalid.push({
						number: onlyDigits,
						originalNumber: trimmed,
						lineNumber: index + 1
					});
				}
			});

			setValidNumbers(valid);
			setInvalidNumbers(invalid);
			setPreviewNumbers([...valid, ...invalid]);
			setSelectedNumbers(new Set(valid.map((_, index) => index)));
			setShowPreview(true);
		} catch (error) {
			toast.error('Erro ao ler o arquivo');
		}
	};

	const handleNumberToggle = (index) => {
		const newSelected = new Set(selectedNumbers);
		if (newSelected.has(index)) {
			newSelected.delete(index);
		} else {
			newSelected.add(index);
		}
		setSelectedNumbers(newSelected);
	};

	const handleSelectAll = () => {
		if (selectedNumbers.size === validNumbers.length) {
			setSelectedNumbers(new Set());
		} else {
			setSelectedNumbers(new Set(validNumbers.map((_, index) => index)));
		}
	};

	const handleImport = () => {
		const numbersToImport = validNumbers
			.filter((_, index) => selectedNumbers.has(index))
			.map(item => item.number);

		if (numbersToImport.length === 0) {
			toast.warning('Selecione pelo menos um número para importar');
			return;
		}

		onImport(numbersToImport, numberType);
		handleClose();
	};

	const handleClose = () => {
		setSelectedFile(null);
		setPreviewNumbers([]);
		setSelectedNumbers(new Set());
		setValidNumbers([]);
		setInvalidNumbers([]);
		setShowPreview(false);
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
		onClose();
	};

	const getNumberTypeLabel = () => {
		return numberType === 'SOMENTE_RESPONDER' ? 'Somente Responder' : 'Não Responder';
	};

	return (
		<Dialog open={isOpen} onOpenChange={handleClose}>
			<DialogContent className="max-w-2xl max-h-[80vh] bg-dashboardCard border-gray-600 text-white overflow-hidden flex flex-col">
				<DialogHeader className="flex-shrink-0">
					<DialogTitle className="text-xl font-bold text-primaryColor flex items-center gap-2">
						<Upload className="w-5 h-5" />
						Importar Números - {getNumberTypeLabel()}
					</DialogTitle>
				</DialogHeader>

				<div className="flex-1 overflow-y-auto">
					{!showPreview ? (
						<div className="space-y-6">
							{/* Instruções */}
							<div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
								<div className="flex items-start gap-3">
									<Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
									<div className="space-y-2">
										<h3 className="font-semibold text-blue-300">Instruções para importação:</h3>
										<ul className="text-sm text-blue-200 space-y-1">
											<li>• O arquivo deve estar no formato <code className="bg-blue-800/50 px-1 rounded">.txt</code></li>
											<li>• Cada número deve estar em uma linha separada</li>
											<li>• Os números serão salvos apenas com dígitos (sem formatação)</li>
											<li>• Exemplos de formatos aceitos:</li>
										</ul>
										<div className="bg-blue-800/30 rounded p-2 mt-2 font-mono text-xs">
											<div>555181995600</div>
											<div>+55 51 8199-5600</div>
											<div>(51) 81995600</div>
											<div>51 8199-5600</div>
											<div className="text-blue-300 mt-2 text-xs">→ Todos serão salvos como: 555181995600</div>
										</div>
									</div>
								</div>
							</div>

							{/* Seleção de arquivo */}
							<div className="space-y-4">
								<h3 className="font-semibold flex items-center gap-2">
									<FileText className="w-4 h-4" />
									Selecionar Arquivo
								</h3>

								<div className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center">
									<Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
									<p className="text-gray-300 mb-4">
										Clique para selecionar um arquivo .txt com os números
									</p>
									<input
										ref={fileInputRef}
										type="file"
										accept=".txt"
										onChange={handleFileSelect}
										className="hidden"
									/>
									<button
										onClick={() => fileInputRef.current?.click()}
										className="bg-primaryColor hover:bg-primaryColor/80 text-white px-6 py-2 rounded-lg transition-colors"
									>
										Escolher Arquivo
									</button>
								</div>

								{selectedFile && (
									<div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3">
										<p className="text-green-300 text-sm">
											✓ Arquivo selecionado: <span className="font-mono">{selectedFile.name}</span>
										</p>
									</div>
								)}
							</div>
						</div>
					) : (
						<div className="space-y-4">
							{/* Resumo */}
							<div className="grid grid-cols-3 gap-4">
								<div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3 text-center">
									<div className="text-2xl font-bold text-green-400">{validNumbers.length}</div>
									<div className="text-sm text-green-300">Números Válidos</div>
								</div>
								<div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 text-center">
									<div className="text-2xl font-bold text-red-400">{invalidNumbers.length}</div>
									<div className="text-sm text-red-300">Números Inválidos</div>
								</div>
								<div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 text-center">
									<div className="text-2xl font-bold text-blue-400">{selectedNumbers.size}</div>
									<div className="text-sm text-blue-300">Selecionados</div>
								</div>
							</div>

							{/* Controles */}
							<div className="flex justify-between items-center">
								<div className="flex items-center gap-2">
									<button
										onClick={handleSelectAll}
										className="text-sm bg-gray-600 hover:bg-gray-500 px-3 py-1 rounded transition-colors"
									>
										{selectedNumbers.size === validNumbers.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
									</button>
									<span className="text-sm text-gray-400">
										(apenas números válidos)
									</span>
								</div>
							</div>

							{/* Lista de números */}
							<div className="border border-gray-600 rounded-lg max-h-60 overflow-y-auto">
								<div className="sticky top-0 bg-dashboardCard border-b border-gray-600 px-4 py-2">
									<div className="grid grid-cols-12 gap-2 text-sm font-semibold text-gray-300">
										<div className="col-span-1">Sel.</div>
										<div className="col-span-1">Linha</div>
										<div className="col-span-4">Original</div>
										<div className="col-span-3">Será Salvo</div>
										<div className="col-span-3">Status</div>
									</div>
								</div>
								<div className="p-2 space-y-1">
									{validNumbers.map((item, index) => (
										<div
											key={`valid-${index}`}
											className={`grid grid-cols-12 gap-2 p-2 rounded text-sm items-center ${
												selectedNumbers.has(index) ? 'bg-green-900/20' : 'hover:bg-gray-700/50'
											}`}
										>
											<div className="col-span-1">
												<input
													type="checkbox"
													checked={selectedNumbers.has(index)}
													onChange={() => handleNumberToggle(index)}
													className="w-4 h-4"
												/>
											</div>
											<div className="col-span-1 text-gray-400 text-xs">
												{item.lineNumber}
											</div>
											<div className="col-span-4 font-mono text-xs text-gray-300">
												{item.originalNumber}
											</div>
											<div className="col-span-3 font-mono text-xs text-green-300">
												{item.number}
											</div>
											<div className="col-span-3">
												<span className="inline-flex items-center gap-1 text-green-400 text-xs">
													<Check className="w-3 h-3" />
													Válido
												</span>
											</div>
										</div>
									))}
									{invalidNumbers.map((item, index) => (
										<div
											key={`invalid-${index}`}
											className="grid grid-cols-12 gap-2 p-2 rounded text-sm items-center bg-red-900/10"
										>
											<div className="col-span-1">
												<input
													type="checkbox"
													disabled
													className="w-4 h-4 opacity-50"
												/>
											</div>
											<div className="col-span-1 text-gray-400 text-xs">
												{item.lineNumber}
											</div>
											<div className="col-span-4 font-mono text-xs text-gray-500">
												{item.originalNumber}
											</div>
											<div className="col-span-3 font-mono text-xs text-red-400">
												{item.number}
											</div>
											<div className="col-span-3">
												<span className="inline-flex items-center gap-1 text-red-400 text-xs">
													<AlertTriangle className="w-3 h-3" />
													Inválido
												</span>
											</div>
										</div>
									))}
								</div>
							</div>
						</div>
					)}
				</div>

				{/* Footer */}
				<div className="flex-shrink-0 flex justify-end gap-3 pt-4 border-t border-gray-600">
					<button
						onClick={handleClose}
						className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
					>
						Cancelar
					</button>

					{showPreview && (
						<>
							<button
								onClick={() => setShowPreview(false)}
								className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
							>
								Voltar
							</button>
							<button
								onClick={handleImport}
								disabled={selectedNumbers.size === 0 || isImporting}
								className="px-6 py-2 bg-primaryColor hover:bg-primaryColor/80 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
							>
								{isImporting && (
									<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
								)}
								Importar {selectedNumbers.size} Números
							</button>
						</>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};
